import {ActionIcon, Alert, Badge, Button, Group, Paper, Select, Stack, Text, Title} from "@mantine/core";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import axios from "axios";
import { FloppyDiskIcon, TrashIcon } from "@phosphor-icons/react";
import {useContext, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../../components/Loader/LoaderScreen.tsx";
import Page from "../../../components/Page/Page.tsx";
import MessageContext from "../../../context/MessageContext.tsx";
import api, {useFactions} from "../../../utils/api.ts";
import ProductionModuleNav from "./ProductionModuleNav.tsx";
import {ProductionModuleInfo} from "./types.ts";

interface PublicUser {
    uid: string;
    handle: string;
}

interface AccessResponse {
    module: ProductionModuleInfo;
}

function ProductionModuleAccessPage() {
    const {moduleId} = useParams<{moduleId?: string}>();
    const queryClient = useQueryClient();
    const {success, error} = useContext(MessageContext);
    const [selectedUser, setSelectedUser] = useState<string | null>(null);
    const [accessIds, setAccessIds] = useState<string[]>([]);

    const accessQuery = useQuery<AccessResponse>({
        queryKey: ['modules', 'production', 'access', moduleId],
        queryFn: async () => {
            const response = await api.get(`/modules/production/${moduleId}/access`);
            return response.data as AccessResponse;
        },
        enabled: !!moduleId,
    });

    const usersQuery = useQuery<PublicUser[]>({
        queryKey: ['users', 'public'],
        queryFn: async () => {
            const response = await api.get('/users');
            return response.data as PublicUser[];
        },
        staleTime: 1000 * 60 * 5,
    });

    const {factions} = useFactions();

    useEffect(() => {
        const accessList = accessQuery.data?.module.access ?? [];
        setAccessIds(accessList.map((entry) => entry.id));
    }, [accessQuery.data?.module.access]);

    const usersById = useMemo(() => {
        const map = new Map<string, string>();
        (usersQuery.data ?? []).forEach((user) => {
            map.set(user.uid, user.handle);
        });
        return map;
    }, [usersQuery.data]);

    const availableUsers = useMemo(() => {
        const module = accessQuery.data?.module;
        return (usersQuery.data ?? [])
            .filter((user) => {
                if (module?.ownerType === 'user' && module.owner === user.uid) {
                    return false;
                }
                return !accessIds.includes(user.uid);
            })
            .sort((a, b) => a.handle.localeCompare(b.handle));
    }, [usersQuery.data, accessIds, accessQuery.data?.module]);

    const selectData = useMemo(() => {
        return availableUsers.map((user) => ({value: user.uid, label: user.handle}));
    }, [availableUsers]);

    const saveMutation = useMutation({
        mutationFn: async () => {
            if (!moduleId) {
                return null;
            }
            const response = await api.post(`/modules/production/${moduleId}/access`, {access: accessIds});
            return response.data as AccessResponse;
        },
        onSuccess: () => {
            success('Access updated');
            queryClient.invalidateQueries({queryKey: ['modules', 'production', 'access', moduleId]});
            queryClient.invalidateQueries({queryKey: ['modules', 'production']});
        },
        onError: (e: any) => {
            error('Could not save access list', e?.response?.data?.error ?? e.message);
        },
    });
    const module = accessQuery.data?.module;

    const factionName = useMemo(() => {
        if(!module || module.ownerType !== 'faction') return null
        return factions.find((f) => f.id === module.owner)?.name ?? module.owner
    }, [module?.ownerType, module?.owner, factions])

    if (!moduleId) {
        return (
            <Page>
                <Alert color="red" title="Missing module ID">
                    No production module was selected.
                </Alert>
            </Page>
        );
    }

    if (accessQuery.isPending || usersQuery.isPending) {
        return <LoaderScreen message="Loading module access"/>;
    }

    if (accessQuery.isError) {
        const statusCode = axios.isAxiosError(accessQuery.error) ? accessQuery.error.response?.status : null;
        return (
            <Page>
                <Alert color="red" title="Access unavailable">
                    {statusCode === 403
                        ? 'Only module owners can manage the access list.'
                        : 'Could not load module access details.'}
                </Alert>
            </Page>
        );
    }

    if (usersQuery.isError) {
        return (
            <Page>
                <Alert color="red" title="Users unavailable">
                    Could not load user list for access management.
                </Alert>
            </Page>
        );
    }


    if (!module) {
        return null;
    }

    return (
        <Page>
            <title>{`${module.label} - Access`}</title>
            <Stack gap="md">
                <div>
                    <Title order={1}>Manage Module Access</Title>
                    <Text c="dimmed">{module.label} ({module.id})</Text>
                </div>
                <ProductionModuleNav moduleId={moduleId}/>

                <Paper withBorder p="md">
                    <Group justify="space-between" align="center" wrap="nowrap">
                        <div>
                            <Text size="sm" c="dimmed">Owner</Text>
                            <Text fw={600}>
                                {module.ownerType === 'faction'
                                    ? (factionName)
                                    : (usersById.get(module.owner) ?? module.owner)}
                            </Text>
                        </div>
                        <Badge variant="light" color={module.ownerType === 'faction' ? 'blue' : 'grape'}>
                            {module.ownerType}
                        </Badge>
                    </Group>
                </Paper>

                <Paper withBorder p="md">
                    <Group align="end" wrap="nowrap">
                        <Select
                            value={selectedUser}
                            onChange={setSelectedUser}
                            data={selectData}
                            label="Add user"
                            placeholder="Select a user"
                            searchable
                            style={{flexGrow: 1}}
                        />
                        <Button
                            onClick={() => {
                                if (!selectedUser || accessIds.includes(selectedUser)) {
                                    return;
                                }
                                setAccessIds((current) => [...current, selectedUser]);
                                setSelectedUser(null);
                            }}
                            disabled={!selectedUser}
                        >
                            Add
                        </Button>
                        <Button
                            leftSection={<FloppyDiskIcon size={16}/>}
                            onClick={() => saveMutation.mutate()}
                            loading={saveMutation.isPending}
                        >
                            Save
                        </Button>
                    </Group>
                </Paper>

                <Paper withBorder p="md">
                    <Text fw={700}>Users with access</Text>
                    <Stack gap="xs" mt="sm">
                        {accessIds.length === 0 ? (
                            <Text size="sm" c="dimmed">No additional users have access yet.</Text>
                        ) : null}

                        {accessIds.map((id) => (
                            <Group key={id} justify="space-between" wrap="nowrap">
                                <Text>{usersById.get(id) ?? id}</Text>
                                <ActionIcon
                                    color="red"
                                    variant="subtle"
                                    onClick={() => setAccessIds((current) => current.filter((item) => item !== id))}
                                >
                                    <TrashIcon size={16}/>
                                </ActionIcon>
                            </Group>
                        ))}
                    </Stack>
                </Paper>
            </Stack>
        </Page>
    );
}

export default withRouteErrorBoundary(ProductionModuleAccessPage);
